import { type FC, type FormEvent, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Dialog from "@mui/material/Dialog";
import { DateTimePicker } from "@mui/x-date-pickers";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import { ProductTypeType } from "@/types/graphql/partner.generated";
import { useIsMobile } from "@/shared/hooks";
import type { ProductFormPrice, ProductFormPricePeriod } from "../model";
import { createLocalKey, isPriceValid } from "../model";
import { CountryAccessSelect } from "./CountryAccessSelect";
import { CurrencyField } from "./CurrencyField";

const SUBSCRIPTION_PERIODS = [7, 30, 90, 180, 365];

const SUBSCRIPTION_PERIOD_KEYS: Record<number, string> = {
  7: "products.priceEditDialog.periodWeekly",
  30: "products.priceEditDialog.periodMonthly",
  90: "products.priceEditDialog.period3Month",
  180: "products.priceEditDialog.periodHalfYear",
  365: "products.priceEditDialog.periodYearly",
};

interface PriceEditDialogProps {
  open: boolean;
  price: ProductFormPrice;
  productType: ProductTypeType;
  isNew: boolean;
  onClose: () => void;
  onSubmit: (price: ProductFormPrice) => void;
}

/**
 * Диалог создания/редактирования одной цены продукта (референс:
 * PricesField/PriceEditDialog/PriceEditDialog.js). Редактирует ЛОКАЛЬНУЮ
 * копию цены — сохранение мержит результат в prices формы по индексу/ключу
 * (см. PricesField). Без ExchangeRate-подсказок, InAppProductField,
 * minimumBillingCycles, custom title, preOrder, XTR/valueUSD — см. отчёт.
 */
export const PriceEditDialog: FC<PriceEditDialogProps> = ({
  open,
  price,
  productType,
  isNew,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [draft, setDraft] = useState<ProductFormPrice>(price);
  const isSubscription = productType === ProductTypeType.SubscriptionType;

  const title = isSubscription
    ? isNew
      ? t("products.priceEditDialog.newSubscriptionTitle")
      : t("products.priceEditDialog.editSubscriptionTitle")
    : isNew
      ? t("products.priceEditDialog.newTicketTitle")
      : t("products.priceEditDialog.editTicketTitle");

  const patch = (next: Partial<ProductFormPrice>) =>
    setDraft((current) => ({ ...current, ...next }));

  const patchPeriod = (index: number, next: Partial<ProductFormPricePeriod>) =>
    setDraft((current) => ({
      ...current,
      periods: current.periods.map((period, i) =>
        i === index ? { ...period, ...next } : period
      ),
    }));

  const handleAddPeriod = () => {
    setDraft((current) => ({
      ...current,
      periods: [
        ...current.periods,
        { key: createLocalKey(), dateFrom: null, value: "", oldValue: "" },
      ],
    }));
  };

  const handleRemovePeriod = (index: number) => {
    setDraft((current) => ({
      ...current,
      periods: current.periods.filter((_, i) => i !== index),
    }));
  };

  // Гейт кнопки «Сохранить» (référence: PriceEditDialog.js оборачивает форму
  // в <form onSubmit>, а не «голую» кнопку с onClick) — период задан и
  // КАЖДОЕ окно валидно (dateFrom задан, value — непустое конечное число).
  // Без этого гейта пустое value уходило бы в мутацию как Number("")===0
  // (молча создавалась цена $0), а нечисловой ввод — как NaN.
  const canSave = isPriceValid(draft, productType);

  const handleFormSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canSave) return;
    onSubmit(draft);
  };

  const sortedPeriods = [...draft.periods].sort((a, b) => {
    if (!a.dateFrom || !b.dateFrom) return 0;
    return new Date(a.dateFrom).getTime() - new Date(b.dateFrom).getTime();
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="md"
      scroll="body"
    >
      <form onSubmit={handleFormSubmit}>
        <AppBar color="default" position="sticky" elevation={0}>
          <Toolbar>
            <IconButton
              type="button"
              edge="start"
              onClick={onClose}
              sx={{ mr: 1 }}
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" noWrap sx={{ flex: 1 }}>
              {title}
            </Typography>
            <Button variant="contained" type="submit" disabled={!canSave}>
              {t("products.priceEditDialog.saveButton")}
            </Button>
          </Toolbar>
          <Divider />
        </AppBar>

        <Box sx={{ p: 1 }}>
          <Grid container spacing={1}>
            <Grid size={12}>
              {/* référence: PriceEditDialog.js — карточки диалога цены БЕЗ
                  elevation={0} (с тенью, как обычные Card). */}
              <Card>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <CountryAccessSelect
                        value={draft.countryAccessId}
                        onChange={(value) => patch({ countryAccessId: value })}
                      />
                    </Grid>

                    {!isSubscription && (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            required
                            type="number"
                            label={t("products.priceEditDialog.periodTicket")}
                            fullWidth
                            variant="outlined"
                            value={draft.period ?? ""}
                            onChange={(event) =>
                              patch({
                                period: event.target.value
                                  ? parseInt(event.target.value, 10)
                                  : null,
                              })
                            }
                            slotProps={{
                              htmlInput: { inputMode: "numeric", min: 1 },
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {t("products.priceEditDialog.days", {
                                      count: draft.period
                                        ? Number(draft.period)
                                        : 0,
                                    })}
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CurrencyField
                            required
                            label={t("products.priceEditDialog.currency")}
                            value={draft.currency}
                            onChange={(value) => patch({ currency: value })}
                          />
                        </Grid>
                      </>
                    )}

                    {isSubscription && (
                      <>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            required
                            select
                            label={t(
                              "products.priceEditDialog.periodSubscription"
                            )}
                            fullWidth
                            variant="outlined"
                            value={draft.period ?? ""}
                            onChange={(event) =>
                              patch({
                                period: parseInt(event.target.value, 10),
                              })
                            }
                          >
                            {SUBSCRIPTION_PERIODS.map((period) => (
                              <MenuItem key={period} value={period}>
                                {t(SUBSCRIPTION_PERIOD_KEYS[period])}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <CurrencyField
                            required
                            label={t("products.priceEditDialog.currency")}
                            value={draft.currency}
                            onChange={(value) => patch({ currency: value })}
                            productType={productType}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            type="number"
                            label={t("products.priceEditDialog.trialPeriod")}
                            fullWidth
                            variant="outlined"
                            value={draft.trialPeriod ?? ""}
                            onChange={(event) =>
                              patch({
                                trialPeriod: event.target.value
                                  ? parseInt(event.target.value, 10)
                                  : null,
                              })
                            }
                            slotProps={{
                              htmlInput: { inputMode: "numeric", min: 1 },
                              input: {
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {t("products.priceEditDialog.days", {
                                      count: draft.trialPeriod
                                        ? Number(draft.trialPeriod)
                                        : 0,
                                    })}
                                  </InputAdornment>
                                ),
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            label={t("products.priceEditDialog.trialValue")}
                            fullWidth
                            variant="outlined"
                            value={draft.trialValue}
                            onChange={(event) =>
                              patch({ trialValue: event.target.value })
                            }
                            slotProps={{
                              htmlInput: { inputMode: "numeric" },
                            }}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {sortedPeriods.map((period) => {
              const index = draft.periods.findIndex(
                (p) => p.key === period.key
              );
              return (
                <Grid key={period.key} size={12}>
                  {/* référence: PriceEditDialog.js — карточки диалога цены БЕЗ
                  elevation={0} (с тенью, как обычные Card). */}
                  <Card>
                    <CardContent>
                      <Grid container spacing={2} alignItems="flex-start">
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <DateTimePicker
                            label={t("products.priceEditDialog.dateFrom")}
                            value={
                              period.dateFrom ? moment(period.dateFrom) : null
                            }
                            onChange={(value) =>
                              patchPeriod(index, {
                                dateFrom: value ? value.toISOString() : null,
                              })
                            }
                            slotProps={{
                              textField: { fullWidth: true, required: true },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <TextField
                            required
                            label={t("products.priceEditDialog.value")}
                            fullWidth
                            variant="outlined"
                            value={period.value}
                            onChange={(event) =>
                              patchPeriod(index, { value: event.target.value })
                            }
                            slotProps={{
                              htmlInput: { inputMode: "numeric" },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <TextField
                            label={t("products.priceEditDialog.oldValue")}
                            fullWidth
                            variant="outlined"
                            value={period.oldValue}
                            onChange={(event) =>
                              patchPeriod(index, {
                                oldValue: event.target.value,
                              })
                            }
                            slotProps={{
                              htmlInput: { inputMode: "numeric" },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 1 }}>
                          <IconButton
                            type="button"
                            onClick={() => handleRemovePeriod(index)}
                            disabled={draft.periods.length === 1}
                            size="large"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}

            <Grid size={12}>
              {/* référence: PriceEditDialog.js — карточки диалога цены БЕЗ
                  elevation={0} (с тенью, как обычные Card). */}
              <Card>
                <CardContent>
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={handleAddPeriod}
                  >
                    {t("products.priceEditDialog.addPeriodButton")}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Dialog>
  );
};
